import { useLocation, useNavigate } from "react-router-dom";
import SettingsShell from "../../components/SettingsShell";
import SellerSetupChecklist from "../../components/SellerSetupChecklist";
import {
  ProfileInfoPanel, BusinessDetailsPanel, PaymentBillingPanel,
  ShippingAddressesPanel, NotificationsPanel, TeamMembersPanel,
  SecurityPanel, DefaultTermsPanel,
} from "../../components/SettingsPanels";
import { BrandingPanel, DomainPanel, OperationsPanel, PayoutSchedulePanel, TaxReportsPanel, PublicProfilePanel } from "./SellerSettingsPanels";
import SellerCatalogApiPanel from "../../components/seller/SellerCatalogApiPanel";
import BuyerPricingPanel from "../../components/seller/BuyerPricingPanel";
import { PaymentApprovalsPanel } from "../../components/PaymentApprovalsPanel";
import PlanBillingPanel from "../../components/PlanBillingPanel";
import AutoDebitAuthorizationCard from "../../components/AutoDebitAuthorizationCard";
import KycVerificationPanel from "../../components/KycVerificationPanel";
import { useAuth } from "../../context/AuthContext";
import { useSellerSetup } from "../../context/SellerSetupContext";
import { useSellerBilling } from "../../context/SellerBillingContext";
import {
  User, Building2, Palette, Globe, CreditCard, Handshake,
  Truck, Plug, Bell, Users, Shield, Settings2, ShieldCheck,
  CalendarClock, FileText, Store, Sparkles, Tag,
} from "lucide-react";

/* Seller Settings — left-side tab navigation covering 14 sections. */
export default function SellerSettings() {
  const { user } = useAuth();
  const { refresh: refreshSetup } = useSellerSetup();
  const { status: billing } = useSellerBilling();
  // Master kill switch — pre-launch, hide the entire Plan & Billing tab
  // from the seller settings nav. Flipping it ON makes the tab appear
  // without a deploy.
  const saasBillingLive = billing?.enforced !== false;
  const location = useLocation();
  const nav = useNavigate();
  // Default-true for legacy users (no team_flags = solo owner).
  const flags = user?.team_flags || {};
  const canBanking  = flags.can_banking  !== false;  // manage banks + payout
  const canSettings = flags.can_settings !== false;  // change default terms etc.
  const canMoney    = flags.can_money    !== false;  // wallet pay/topup/withdraw
  const tabs = [
    { id: "profile",    label: "Profile info",      icon: User,       group: "Account",    render: () => <ProfileInfoPanel/> },
    ...(canSettings ? [
      { id: "business",   label: "Business details",  icon: Building2,                       render: () => <BusinessDetailsPanel/> },
      // Phase WP3.2 · Wyzia KYC step — blocking onboarding requirement.
      { id: "kyc",        label: "Identity & bank",   icon: ShieldCheck,                     render: () => <KycVerificationPanel/> },
    ] : []),

    ...(canSettings ? [
      { id: "branding",   label: "Branding",          icon: Palette,    group: "Portal",     render: () => <BrandingPanel/> },
      { id: "public-profile", label: "Public profile", icon: Store,                          render: () => <PublicProfilePanel/> },
      { id: "domain",     label: "Domain",            icon: Globe,                           render: () => <DomainPanel/> },
    ] : []),

    ...(canSettings ? [
      { id: "operations", label: "Operations",        icon: Settings2,  group: "Operations", render: () => <OperationsPanel/> },
      { id: "shipping",   label: "Warehouse addresses", icon: Truck,                         render: () => <ShippingAddressesPanel labelSingular="warehouse" titleLabel="Warehouse addresses" descLabel="Your default warehouse and any additional fulfillment locations."/> },
    ] : []),

    // Finance group — split by capability so Admins see the panels they
    // can use (payment & billing read + payouts read) and Owners see all.
    ...(canSettings ? [
      { id: "terms",      label: "Default terms",     icon: Handshake,  group: "Finance",    render: () => <DefaultTermsPanel/> },
    ] : []),
    ...(canBanking || canMoney ? [
      { id: "payment",    label: "Payment & billing", icon: CreditCard, group: "Finance",    render: () => <PaymentBillingPanel/> },
      { id: "bank-auto-debit", label: "Bank auto-debit", icon: CreditCard, group: "Finance", render: () => <AutoDebitAuthorizationCard role="seller"/> },
    ] : []),
    ...(canBanking ? [
      { id: "payouts",    label: "Auto-payout schedule", icon: CalendarClock, group: "Finance", render: () => <PayoutSchedulePanel/> },
    ] : []),
    ...(canMoney ? [
      { id: "tax",        label: "Tax reports (1099-K)", icon: FileText, group: "Finance",  render: () => <TaxReportsPanel/> },
    ] : []),

    // Payment approvals — co-signer workflow for large payments.
    // OWNER-ONLY (Jun 2026): financial governance lives strictly with
    // the account owner. Admins, operators, viewers don't see this tab.
    ...((user?.team_role || "owner") === "owner" ? [
      { id: "payment-approvals", label: "Payment approvals", icon: ShieldCheck, group: "Finance", render: () => <PaymentApprovalsPanel/> },
      // Plan & Billing — verqo SaaS subscription. Owner-only AND gated
      // by the master kill switch (hidden pre-launch).
      ...(saasBillingLive ? [
        { id: "plan-billing", label: "Plan & Billing", icon: Sparkles, group: "Finance", render: () => <PlanBillingPanel role="seller"/> },
      ] : []),
    ] : []),

    { id: "integrations", label: "Integrations & API", icon: Plug,    group: "Automation", render: () => <SellerCatalogApiPanel/> },
    { id: "buyer-pricing", label: "Buyer pricing",     icon: Tag,     group: "Automation", render: () => <BuyerPricingPanel/> },
    { id: "notifications", label: "Notifications",   icon: Bell,                           render: () => <NotificationsPanel/> },

    ...(flags.can_invite !== false ? [
      { id: "team",       label: "Team members",      icon: Users,      group: "Access",     render: () => <TeamMembersPanel/> },
    ] : []),
    { id: "security",   label: "Security",          icon: Shield,       group: flags.can_invite === false ? "Access" : undefined,  render: () => <SecurityPanel/> },
  ];

  // Re-poll setup status when the user moves between tabs — the moment they
  // save a required field, the checklist should reflect the new state.
  const handleTabChange = (tabId) => {
    // Update the URL hash so deep-link / browser-back behave correctly.
    if ((location.hash || "").replace("#", "") !== tabId) {
      nav(`${location.pathname}#${tabId}`, { replace: true });
    }
    // Small delay before refresh so any auto-save mounted-effect has flushed.
    setTimeout(() => refreshSetup(), 300);
  };

  // The checklist's "Open" buttons jump to the relevant tab.
  const jumpToTab = (tabId) => {
    nav(`${location.pathname}#${tabId}`);
    // Smooth scroll back to top of content so the user sees the panel.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SettingsShell
      title="Settings"
      subtitle="Everything about your account, your brand and your wholesale portal — in one place."
      tabs={tabs}
      banner={canSettings ? <SellerSetupChecklist onJumpToTab={jumpToTab}/> : null}
      onTabChange={handleTabChange}
    />
  );
}
